// Pure library filtering. Kept separate from the React page so it can be
// unit-tested. Filter semantics:
//
//   - skillCategory filter: single-select. "all" disables the filter.
//   - techniques filter: multi-select. A video matches if it has at least
//     one of the selected techniques (OR semantics inside the technique
//     set). Across category and techniques, semantics are AND.
//   - Videos that haven't been transcribed yet (no skillCategory, no
//     techniques) are filtered out as soon as ANY filter is active —
//     they simply can't match a tag they don't have.

import { UserVideo } from "./library-store";

export type CategoryFilter = "all" | string;

export interface LibraryFilters {
  category: CategoryFilter;
  techniques: ReadonlySet<string>;
}

export const NO_FILTERS: LibraryFilters = {
  category: "all",
  techniques: new Set<string>(),
};

export function isFilterActive(f: LibraryFilters): boolean {
  return f.category !== "all" || f.techniques.size > 0;
}

export function filterVideos(
  videos: readonly UserVideo[],
  filters: LibraryFilters,
): UserVideo[] {
  if (!isFilterActive(filters)) return [...videos];

  return videos.filter((v) => {
    if (filters.category !== "all") {
      if (v.skillCategory !== filters.category) return false;
    }
    if (filters.techniques.size > 0) {
      const has = v.techniques ?? [];
      const matches = has.some((t) => filters.techniques.has(t));
      if (!matches) return false;
    }
    return true;
  });
}

/** Distinct categories present in the library, sorted alphabetically. */
export function availableCategories(
  videos: readonly UserVideo[],
): readonly string[] {
  const set = new Set<string>();
  for (const v of videos) {
    if (v.skillCategory) set.add(v.skillCategory);
  }
  return Array.from(set).sort();
}

/**
 * Distinct techniques present in the library. Sorted by descending frequency
 * (most-used first) so the chip row puts the user's actual focus first.
 */
export function availableTechniques(
  videos: readonly UserVideo[],
): readonly string[] {
  const counts = new Map<string, number>();
  for (const v of videos) {
    for (const t of v.techniques ?? []) {
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([t]) => t);
}

export function toggleTechnique(
  current: ReadonlySet<string>,
  technique: string,
): Set<string> {
  const next = new Set(current);
  if (next.has(technique)) next.delete(technique);
  else next.add(technique);
  return next;
}
